'use client';

import { useMemo, useState } from 'react';
import {
  useFont, stackFor, CATEGORY_LABEL, type FontOption, type FontCategory,
} from '@/lib/font';

/**
 * Font chooser — the Font section of Settings ▸ Appearance.
 *
 * It used to be an "Aa" button in the header with its own dialog. The header row
 * is for things people do while translating; the reading font is set once and
 * left, so it moved into Settings next to the light/dark choice (owner,
 * 2026-10-13). The list is the same one the dialog had.
 *
 * Three decisions worth stating:
 *
 * 1. **Every option is previewed in its own face.** A list of font names set in
 *    the current font is a list of words, not a choice — the one thing being
 *    decided is what the letters look like, so the letters have to look like it.
 *
 * 2. **Selecting applies immediately, with no Save.** The page around Settings is
 *    the real preview, and the only way to judge a reading font is against real
 *    text at real size.
 *
 * 3. **The list is long and searchable.** It is mostly fonts already on the
 *    reader's machine (see lib/font.tsx), which is how it gets from six entries
 *    to fifty-plus without shipping a byte. Long enough that a search box stops
 *    being a nicety, and grouped by kind so scrolling has landmarks.
 */

const ORDER: FontCategory[] = ['sans', 'serif', 'mono', 'display'];

export function FontList() {
  const { font, setFont, fonts, detected } = useFont();
  const [q, setQ] = useState('');

  const term = q.trim().toLowerCase();
  const matches = useMemo(
    () => (term ? fonts.filter((f) => f.label.toLowerCase().includes(term)) : fonts),
    [fonts, term],
  );

  // Bundled faces stay together at the top under their own heading: they are the
  // only ones guaranteed to look the same on someone else's machine, which is
  // worth saying rather than burying them alphabetically among the rest.
  const bundled = matches.filter((f) => f.bundled);
  const rest = matches.filter((f) => !f.bundled);

  return (
    <div>
      {/* A real sentence rather than a pangram: the point is to see the face doing
          the job this app asks of it — mixed case, punctuation, a number. */}
      <div className="rounded-xl border border-ink-200 bg-ink-50/60 px-4 py-3 dark:border-ink-700 dark:bg-ink-950/40">
        <p className="text-[17px] leading-relaxed text-ink-800 dark:text-ink-200">
          Could you send the report before Friday? I&rsquo;d like to review all 12 pages
          over the weekend.
        </p>
      </div>

      <div className="relative mt-3">
        <svg viewBox="0 0 24 24"
             className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400"
             fill="none" stroke="currentColor" strokeWidth="1.8">
          <circle cx="11" cy="11" r="6.5" />
          <path d="M16 16l4.5 4.5" strokeLinecap="round" />
        </svg>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Search ${fonts.length} fonts…`}
          aria-label="Search fonts"
          className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-9 pr-3 text-sm
                     text-ink-900 placeholder:text-ink-400 focus:border-accent-500 focus:outline-none
                     dark:border-ink-700 dark:bg-ink-950/40 dark:text-ink-dim"
        />
      </div>

      {/* Its own scroll box, so the search stays in reach while the list moves. */}
      <div className="mt-3 max-h-[320px] overflow-y-auto rounded-xl border border-ink-200 px-3 py-2 dark:border-ink-700/70">
        {matches.length === 0 && (
          <p className="py-8 text-center text-sm text-ink-400">
            No font matches “{q.trim()}”.
          </p>
        )}

        {bundled.length > 0 && (
          <Group title="Always available">
            {bundled.map((f) => (
              <Row key={f.id} f={f} on={f.id === font} onPick={setFont} />
            ))}
          </Group>
        )}

        {ORDER.map((cat) => {
          const inCat = rest.filter((f) => f.category === cat);
          if (!inCat.length) return null;
          return (
            <Group key={cat} title={CATEGORY_LABEL[cat]}>
              {inCat.map((f) => (
                <Row key={f.id} f={f} on={f.id === font} onPick={setFont} />
              ))}
            </Group>
          );
        })}
      </div>
      <p className="mt-2 text-[11px] leading-relaxed text-ink-400">
        {detected
          ? `${fonts.length} fonts — the ones we ship, plus the ones already installed on this device. Remembered on this device.`
          : 'Checking which fonts are installed on this device…'}
      </p>
    </div>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-3 last:mb-0">
      <h3 className="sticky top-0 z-10 -mx-1 bg-white/95 px-1 py-1.5 text-[10px] font-semibold
                     uppercase tracking-[0.12em] text-ink-400 backdrop-blur dark:bg-ink-900/95">
        {title}
      </h3>
      <ul className="space-y-1">{children}</ul>
    </section>
  );
}

function Row({
  f,
  on,
  onPick,
}: {
  f: FontOption;
  on: boolean;
  onPick: (id: string) => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onPick(f.id)}
        aria-pressed={on}
        className={
          'flex w-full items-center gap-3 rounded-xl border px-3.5 py-2 text-left transition ' +
          (on
            ? 'border-accent-500 bg-accent-200 dark:border-accent-500 dark:bg-accent-500/10'
            : 'border-transparent hover:border-ink-200 hover:bg-ink-50 ' +
              'dark:hover:border-ink-700 dark:hover:bg-ink-800/60')
        }
      >
        <span className="min-w-0 flex-1">
          {/* The preview IS the label — set in the face it names, at a size big
              enough to judge. The note stays in the UI font so the two do not
              compete for the same glance. */}
          <span
            className="block truncate text-[17px] leading-snug text-ink-900 dark:text-ink-50"
            style={{ fontFamily: stackFor(f) }}
          >
            {f.label}
          </span>
          {f.note && (
            <span className="mt-0.5 block text-xs text-ink-500 dark:text-ink-400">{f.note}</span>
          )}
        </span>

        {on ? (
          <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0 text-accent-600 dark:text-accent-400"
               fill="none" stroke="currentColor" strokeWidth="2.2">
            <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <span className="h-5 w-5 shrink-0" aria-hidden />
        )}
      </button>
    </li>
  );
}
